<template>
  <div class="col-12 card-wrapper">
    <div class="card mb-4">
      <!-- Card header -->
      <div class="card-header">
        <h3 class="mb-0">
          <span>{{ title }}</span>
          <badge v-if="formData.state" :type="getColor()" class="float-right">
            {{ formData.state.label }}
          </badge>
        </h3>
      </div>
      <!-- Card body -->
      <div class="card-body">
        <div v-if="campaign.articles && campaign.articles.length === 0" class="row mb-3">
          <div class="col-12 text-center">
            <h4 class="text-danger">La campaña seleccionada no posee articulos cargados.</h4>
          </div>
        </div>

        <!-- Form groups used in grid -->
        <form ref="form" class="needs-validation" @submit.prevent="saveOrder">
          <div v-if="newOrder" class="row mb-3">
            <div class="col-3 offset-9">
              <button class="btn btn-link btn-sm btn-fill btn-wd float-right" @click="goBack($event)">
                <i class="fas fa-chevron-left mr-1"></i>Volver
              </button>
            </div>
          </div>

          <order-info
            :campaign="campaign"
            :order-date="formData.date"
            :order-number="formData.code"
            :projection="projection"
            :producer="producer"
          />

          <hr />

          <div v-if="!edit && canEditOrder" class="row mb-0">
            <div class="col-3 offset-9">
              <button title="Editar" class="btn btn-link btn-sm btn-fill btn-wd float-right" @click="edit = true">
                <i class="fas fa-pen mr-1"></i>Editar
              </button>
            </div>
          </div>

          <order-header v-if="!editHeader" :order="order" />

          <div v-if="editHeader" class="row mt-4">
            <div class="col-md-6 col-sm-6 col-xs-12">
              <div class="form-group">
                <label class="control-label"> Fecha Facturación (*) </label>

                <el-date-picker
                  v-model="formData.billingDate"
                  v-validate="modelValidations.billingDate"
                  align="center"
                  :editable="false"
                  format="dd-MM-yyyy"
                  name="billingDate"
                  placeholder="Fecha Facturación"
                  suffix-icon="fas fa-calendar-alt"
                  type="date"
                  value-format="yyyy-MM-dd"
                >
                </el-date-picker>

                <small v-show="billingDate.invalid" class="text-danger">
                  {{ getError('billingDate') }}
                </small>
              </div>

              <div class="form-group">
                <label class="control-label"> Fecha Primer Pago (*) </label>

                <el-date-picker
                  v-model="formData.firstPaymentDate"
                  v-validate="modelValidations.firstPaymentDate"
                  align="center"
                  :editable="false"
                  format="dd-MM-yyyy"
                  name="firstPaymentDate"
                  placeholder="Fecha Primer Pago"
                  suffix-icon="fas fa-calendar-alt"
                  type="date"
                  value-format="yyyy-MM-dd"
                >
                </el-date-picker>

                <small v-show="firstPaymentDate.invalid" class="text-danger">
                  {{ getError('firstPaymentDate') }}
                </small>
              </div>

              <div class="form-group">
                <label class="control-label"> Cantidad de Pagos (*) </label>

                <el-input
                  v-model="formData.payments"
                  v-validate="modelValidations.payments"
                  name="payments"
                  pattern="[0-9]+"
                  placeholder="Cantidad de Pagos"
                  type="number"
                >
                </el-input>

                <small v-show="payments.invalid" class="text-danger">
                  {{ getError('payments') }}
                </small>
              </div>
            </div>

            <div class="col-md-6 col-sm-6 col-xs-12">
              <div class="form-group">
                <label class="control-label"> Fecha Cotización Dólar (*) </label>

                <el-date-picker
                  v-model="formData.dollarQuoteDate"
                  v-validate="modelValidations.dollarQuoteDate"
                  align="center"
                  :editable="false"
                  :picker-options="datePickerOptions"
                  format="dd-MM-yyyy"
                  name="dollarQuoteDate"
                  placeholder="Fecha Cotización Dólar"
                  suffix-icon="fas fa-calendar-alt"
                  type="date"
                  value-format="yyyy-MM-dd"
                  @change="getDollarQuotation"
                >
                </el-date-picker>

                <small v-show="dollarQuoteDate.invalid" class="text-danger">
                  {{ getError('dollarQuoteDate') }}
                </small>
              </div>

              <div class="form-group">
                <label class="control-label"> Cotización Dólar (*) </label>
                <div class="row justify-content-center align-items-center">
                  <div class="col-md-6 col-sm-8 col-xs-12">
                    <el-select
                      v-model="formData.dollarQuotationType"
                      v-validate="modelValidations.dollarQuotationType"
                      name="dollarQuotationType"
                      placeholder="Seleccione Tipo de Cotización (*)"
                      autocomplete="off"
                      filterable
                      @change="changeDollarQuotationType"
                    >
                      <el-option
                        v-for="option in dollarOptions"
                        :key="option.value"
                        :label="option.label"
                        :value="option.value"
                      >
                      </el-option>
                    </el-select>
                  </div>

                  <div class="col-md-6 col-sm-8 col-xs-12">
                    <template v-if="$can('all', 'dollar_quotations')">
                      <el-input
                        v-model="formData.dollarQuote"
                        v-validate="'min_value:' + minValueDollarQuote + '|required'"
                        name="dollarQuote"
                        pattern="[0-9]+([\.,][0-9]+)?"
                        placeholder="Cotización Dólar"
                        step="0.01"
                        type="number"
                        suffix-icon="fas fa-dollar-sign"
                        @change="clearDollarQuotation"
                      >
                      </el-input>

                      <small v-show="dollarQuote.invalid" class="text-danger">
                        {{ getError('dollarQuote') }}
                      </small>
                    </template>

                    <h1 v-else class="font-weight-bolder">{{ getDollarQuote | toCurrencyARS }} .-</h1>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label class="control-label"> Observaciones </label>

                <el-input
                  v-model="formData.comment"
                  v-validate="modelValidations.comment"
                  :autosize="{ minRows: 5 }"
                  name="comment"
                  placeholder="Ingrese una observación"
                  type="textarea"
                >
                </el-input>

                <small v-show="comment.invalid" class="text-danger">
                  {{ getError('comment') }}
                </small>
              </div>
            </div>

            <div class="col-md-12 text-right">
              <base-button native-type="submit" type="primary">Siguiente</base-button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <div v-if="!editHeader" class="card mb-4">
      <div class="card-body">
        <order-item-form
          v-if="canEditItems"
          ref="itemForm"
          :campaign="campaign"
          :order="orderId"
          :projection="projection"
          @addItem="addItem"
        />

        <order-items-list
          v-if="hasItems"
          :add="orderOpen"
          :dollar-quote="getDollarQuote"
          :edit="canEditItems"
          :items="formData.items"
          :order-id="orderId"
          @edit="toggleEditItems"
        />

        <div class="row mt-3">
          <div v-if="canEditItems" class="col-md-12 text-right">
            <base-button v-if="hasItems" type="primary" @click="toggleEditItems">
              <span v-if="orderOpen">Siguiente</span>
              <span v-else>Guardar Cambios</span>
            </base-button>
          </div>
          <div v-else-if="orderOpen" class="col-md-12 text-right">
            <base-button v-if="hasItems" type="success" @click="confirmOrder">Confirmar Pedido</base-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Select, Option, DatePicker } from 'element-ui';
import moment from 'moment';
import { mapActions, mapState, mapGetters } from 'vuex';
import { mapFields } from 'vee-validate';

import OrdersService from '@/services/resources/orders.service';
import { getDollarQuote } from '@/util/orders';

import OrderItemForm from './OrderItemForm';
import OrderItemsList from '../List/OrderItemsList';
import OrderHeader from '../Show/OrderHeader';
import OrderInfo from '../Show/OrderInfo';
import { MANUAL } from '@/util/constants/dollarQuotationTypes';

export default {
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
    OrderItemForm,
    OrderItemsList,
    OrderInfo,
    OrderHeader,
  },

  props: {
    title: {
      type: String,
      default: 'Agregar un nuevo pedido',
    },

    orderId: {
      type: Number,
      default: 0,
    },

    producer: {
      type: Object,
      required: true,
    },

    campaign: {
      type: Object,
      required: true,
    },

    order: {
      type: Object,
      default: () => {
        return {
          code: null,
          date: null,
          firstPaymentDate: null,
          billingDate: null,
          payments: null,
          dollarQuote: null,
          dollarQuoteDate: null,
          dollarQuotationType: null,
          dollarQuotationId: null,
          comment: null,
          statusChangeComment: null,
          state: null,
          items: [],
        };
      },
    },

    projection: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      datePickerOptions: {
        disabledDate: this.disabledDate,
      },

      validated: false,
      edit: true,
      editItems: true,
      statesColors: [],
      formData: {
        code: this.order.code,
        date: this.order.date || new Date(),
        firstPaymentDate: this.order.firstPaymentDate,
        billingDate: this.order.billingDate,
        payments: this.order.payments,
        dollarQuote: this.order.dollarQuote,
        dollarQuoteDate: this.order.dollarQuoteDate,
        dollarQuotationType: this.order.dollarQuotationType,
        dollarQuotationId: this.order.dollarQuotationId,
        dollarQuotation: this.order.dollarQuotation,
        comment: this.order.comment,
        statusChangeComment: this.order.statusChangeComment,
        state: this.order.state,
        items: this.order.items,
      },

      modelValidations: {
        firstPaymentDate: {
          required: true,
        },

        billingDate: {
          required: true,
        },

        payments: {
          required: true,
          numeric: true,
        },

        dollarQuotationType: {
          required: true,
        },

        dollarQuotationId: {
          required: !this.$can('all', 'dollar_quotations'),
        },

        dollarQuoteDate: {
          required: true,
        },

        dollarQuote: {
          required: true,
        },

        comment: {},
        statusChangeComment: {
          required: true,
        },
      },
    };
  },

  computed: {
    ...mapState('dollarQuotation', ['availableDates', 'currentDollarQuotation']),

    ...mapGetters('config', ['dollarSelected', 'dollarOptions']),

    newOrder() {
      return this.orderId === 0;
    },

    editHeader() {
      return this.edit || this.newOrder;
    },

    canEditOrder() {
      return (this.$can('update', 'orders') && this.orderOpen) || this.$can('all', 'orders');
    },

    canEditItems() {
      return this.editItems && this.canEditOrder;
    },

    orderOpen() {
      return this.formData.state !== null && this.formData.state.name === OrdersService._STATE_OPEN;
    },

    hasItems() {
      return this.formData.items.length > 0;
    },

    getDollarQuote() {
      return getDollarQuote(this.formData);
    },

    minValueDollarQuote() {
      return this.formData.dollarQuotationType === MANUAL ? this.currentDollarQuotation.divisa : 0;
    },

    ...mapFields([
      'firstPaymentDate',
      'billingDate',
      'payments',
      'dollarQuote',
      'dollarQuoteDate',
      'dollarQuotationType',
      'dollarQuotationId',
      'comment',
    ]),
  },

  watch: {
    dollarSelected: {
      immediate: true,
      handler() {
        if (this.newOrder) {
          this.formData.dollarQuotationType = this.dollarSelected;
        }
      },
    },

    availableDates: {
      immediate: true,
      deep: true,
      handler(dates) {
        if (this.newOrder) {
          this.formData.dollarQuoteDate = dates.at(-1);
          this.getDollarQuotation();
        }
      },
    },
  },

  mounted() {
    this.fetchAvailableDates();

    this.statesColors = this.$services.resource('order_states').getColors();
    // seteo el modo de edicion para el form
    // cuando se crea un pedido se habilita la edicion
    // cuando se edita un pedido se habilita la vista de detalle
    this.edit = this.newOrder;
  },

  methods: {
    ...mapActions('dollarQuotation', ['fetchAvailableDates']),

    changeDollarQuotationType(value) {
      this.formData.dollarQuotationType = value;
      this.formData.dollarQuotationId = this.formData.dollarQuotation ? this.formData.dollarQuotation.id : null;
      this.formData.dollarQuote = this.getDollarQuote;
    },

    clearDollarQuotation() {
      this.formData.dollarQuotationId = null;
      this.formData.dollarQuotation = null;
    },

    getDollarQuotation() {
      const selectedDate = moment(this.formData.dollarQuoteDate);

      this.$services
        .resource('dollar_quotations')
        .get({ 'filter[date]': selectedDate.format('YYYY-MM-DD') })
        .then((res) => res.data.data)
        .then(([quotation]) => {
          if (quotation) {
            this.formData.dollarQuotation = quotation;
            this.formData.dollarQuotationId = quotation.id;
            this.formData.dollarQuote = this.getDollarQuote;
          }
        });
    },

    getColor() {
      return this.statesColors[this.formData.state.id - 1];
    },

    addItem(item) {
      this.formData.items.push(item);
      this.$refs.itemForm.clean();
    },

    toggleEditItems() {
      this.editItems = !this.editItems;
    },

    async firstFormSubmit() {
      try {
        return await this.$validator.validateAll();
      } finally {
        this.validated = true;
      }
    },

    saveOrder() {
      this.firstFormSubmit().then((res) => {
        if (res) {
          this.$emit('save', this.formData);
          this.edit = false;
        }
      });
    },

    confirmOrder() {
      this.$emit('confirm', this.formData);
    },

    getError(name) {
      return this.errors.first(name);
    },

    isValid(name) {
      return this.validated && !this.errors.has(name);
    },

    goBack() {
      this.$emit('back');
    },

    disabledDate(date) {
      const dateFormatted = moment(date).format('YYYY-MM-DD');

      return !this.availableDates.includes(dateFormatted);
    },
  },
};
</script>

<style scoped>
.form-group .el-date-editor--date {
  width: 100%;
}
</style>
